
















































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import SectionWrapper from '@/componentsV2/molecules/SectionWrapper/SectionWrapper.vue';
import PerformanceItem, {
  HighlightContent
} from '@/components/molecules/PerformanceItem/PerformanceItem.vue';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';

import { ICardPerformance } from '@/models/performance';
import { ECustomerInactivityStepSlug } from '@/models/customer-inactivity';
import { ETutorial } from '@/models/tutorial';

import PerformanceCustomerCenterService from '@/api/PerformanceCustomerCenterService';
import BusinessService from '@/api/BusinessService';

import eventBus from '@/utils/eventBus';
import DateUtils from '@/utils/DateUtils';
import {
  addOverlay,
  removeElement,
  setCardScrollPosition,
  removeHighlightEffectCard
} from '@/utils/highlighted-card';
import { isIos } from '@/utils/is-ios';

import flagKeys from '@/constants/flagKeys';
import customerInactivity from '@/constants/customerInactivity';

interface ITotalValue extends ICardPerformance {
  counterFractionDigits?: number;
  highlighted?: boolean;
  highlightContent?: HighlightContent;
  showInfoIcon?: boolean;
  actionLabel?: string;
}

@Component({
  components: {
    SectionWrapper,
    PerformanceItem
  }
})
export default class PerformanceCustomerCenterOverview extends Vue {
  @Prop({ default: false }) showHighlightedCard?: boolean;
  @Prop() highlightedCardKey?: string;

  performanceCustomerCenterService = new PerformanceCustomerCenterService();
  businessService = new BusinessService();
  totalValues: Record<string, ITotalValue> = this.cachedOverviewData || {
    totalCustomers: {
      label: '',
      description: '',
      counter: 0,
      disabled: true
    },
    memberEngagement: {
      label: '',
      description: '',
      counter: 0,
      sufixCounter: '%',
      disabled: true
    },
    ...(this.editCustomerInactivityIsEnabled && {
      totalActives: {
        label: '',
        description: '',
        counter: 0,
        linkLabel: '',
        link: '/performance/customer-center?customerListFilter=active',
        disabled: true
      },
      totalInactives: {
        label: '',
        description: '',
        counter: 0,
        linkLabel: '',
        link: '/performance/customer-center?customerListFilter=inactive',
        disabled: true,
        ...(this.editCustomerInactivityIsEnabled && {
          highlighted: false,
          showInfoIcon: true,
          actionLabel: 'Edit',
          highlightContent: {
            key: 'edit-inactivity',
            title: 'Edit customer inactivity',
            description:
              'Customize the time it takes for your customer to be considered inactive. This will help you create more effective activation campaigns.',
            actionLabel: 'Edit inactivity'
          }
        })
      }
    })
  };

  observer: IntersectionObserver | null = null;
  triggerIntersected = false;

  get tPrefix() {
    return 'performance.customerCenterV2.overview';
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business || {};
  }

  get programLaunched() {
    return DateUtils.formattedDate({
      date: this.businessInfo.createdAt,
      applyTimezone: true
    });
  }

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get cachedOverviewData() {
    return this.$store.getters['performance/getCustomerCenterState'].overview;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get editCustomerInactivityIsEnabled() {
    return this.flags.includes(flagKeys.editCustomerInactivity);
  }

  get business() {
    const { business = null } = this.$store.getters['business/getBusinessInfo'];

    return business;
  }

  get customerInactivityDaysConfig() {
    return this.business.inactiveCustomerDays;
  }

  get isImpersonated() {
    return this.$store.getters['manager/isImpersonated'];
  }

  @Watch('showHighlightedCard')
  onChangeShowHighlightedCard(isShow: boolean) {
    if (!isShow) return;

    this.addOverlayElement();
  }

  created() {
    this.getOverview();
  }

  mounted() {
    this.addOverlayElement();

    if (!this.isMobileDevice) return;

    const target = document.querySelector(
      '.performance-customer-center-overview__grid-trigger'
    );

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.triggerIntersected = entry.isIntersecting;
      },
      {
        root: document.querySelector(
          '.performance-customer-center-overview__grid'
        ),
        threshold: 0
      }
    );

    this.observer.observe(target as HTMLElement);
  }

  beforeDestroy() {
    this.removeHighlightEffect();
    if (this.observer) this.observer.disconnect();
  }

  handleHighlightCloseClick() {
    this.removeHighlightEffect();
    this.updateViewedTutorialFlag();
    this.handleGoToCustomerList();
  }

  async updateViewedTutorialFlag() {
    if (this.business.seenInactiveConsumersHover || this.isImpersonated) return;

    await this.businessService.updateBusinessFlagTutorial({
      flag: ETutorial.SEEN_INACTIVE_CONSUMER_HOVER,
      state: true
    });

    this.$store.dispatch('business/SET_BUSINESS_INFO', {
      ...this.business,
      seenInactiveConsumersHover: true
    });
  }

  handleGoToCustomerList() {
    const actionQuery = this.$route.query.action;

    if (actionQuery === 'show-inactive-customers-hover') {
      this.$emit('go-to-customer-list', { filterBy: 'inactive' });
    }
  }

  handleHighlightActionClick(actionKey: string, { infoClicked = false } = {}) {
    const mapper: Record<string, () => void> = {
      'edit-inactivity': () => {
        this.updateViewedTutorialFlag();
        this.removeHighlightEffect();

        this.openCustomerInactivityModal(
          infoClicked
            ? ECustomerInactivityStepSlug.INFO
            : ECustomerInactivityStepSlug.EDIT
        );
      }
    };

    if (!mapper[actionKey]) return;

    mapper[actionKey]();
  }

  openCustomerInactivityModal(slug: ECustomerInactivityStepSlug) {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        icon: 'none',
        hideCancelButton: true,
        confirmEvent: '',
        componentToRender: 'customerInactivityModal',
        mobilePositionBottom: true,
        noBackground: true,
        mobileFullScreen: false
      }),
      {
        initialStep: slug,
        onModalClose: () => {
          this.handleGoToCustomerList();
        },
        onConfirm: () => {
          this.getOverview();

          if (this.$route.query.action) {
            this.handleGoToCustomerList();

            return;
          }

          this.$emit('reload-customer-list');
        }
      }
    );
  }

  addOverlayElement() {
    const merchantViewedTutorial = this.business.seenInactiveConsumersHover;
    const action = this.$route.query.action;

    if (
      this.showHighlightedCard &&
      this.highlightedCardKey &&
      (!merchantViewedTutorial || action === 'show-inactive-customers-hover')
    ) {
      addOverlay('overlay-element');

      this.$lock();
      this.totalValues[this.highlightedCardKey].highlighted = true;

      setCardScrollPosition(
        `[data-tippy=${
          this.totalValues[this.highlightedCardKey].highlightContent!.key
        }]`,
        `.performance-customer-center-overview__grid`
      );
    }
  }

  removeHighlightEffect() {
    this.$unlock();

    removeElement('overlay-element');

    if (isIos() && this.showHighlightedCard && this.highlightedCardKey) {
      removeHighlightEffectCard(
        `[data-tippy=${
          this.totalValues[this.highlightedCardKey].highlightContent!.key
        }]`,
        `.performance-customer-center-overview__grid-item--highlighted`
      );
    }

    if (this.highlightedCardKey && this.totalValues[this.highlightedCardKey]) {
      this.totalValues[this.highlightedCardKey].highlighted = false;
    }
  }

  setTotalValuesInfos(key: string) {
    return {
      label: this.$t(`${this.tPrefix}.cards.${key}.title`),
      description: this.$t(`${this.tPrefix}.cards.${key}.description`, {
        numberOfDays:
          this.customerInactivityDaysConfig || customerInactivity.DEFAULT_DAYS,
        pluralDays: this.$tc(
          'common.pluralize.day',
          this.customerInactivityDaysConfig || customerInactivity.DEFAULT_DAYS
        )
      })
    };
  }

  get transformedTotalValues() {
    const result = Object.entries(this.totalValues).map(([key, value]) => ({
      ...value,
      ...(value.hasOwnProperty('sufixCounter') && {
        sufixCounter: '%'
      }),
      label: this.$t(`${this.tPrefix}.cards.${key}.title`),
      description: this.$t(`${this.tPrefix}.cards.${key}.description`, {
        numberOfDays:
          this.customerInactivityDaysConfig || customerInactivity.DEFAULT_DAYS,
        pluralDays: this.$tc(
          'common.pluralize.day',
          this.customerInactivityDaysConfig || customerInactivity.DEFAULT_DAYS
        )
      })
    }));

    return result;
  }

  async getOverview() {
    if (!this.cachedOverviewData) {
      this.$showLoaderWithLocker();
    }

    const response = await this.performanceCustomerCenterService.getOverview();

    this.$hideLoader();

    if (response.status !== 200) return;

    const dataToUpdate = Object.keys(this.totalValues);

    const entries = Object.entries(response.data);

    let newTotalValues = this.totalValues;

    for (const entry of entries) {
      const [key, value] = entry;

      if (!dataToUpdate.includes(key)) continue;

      newTotalValues[key].counter = Number(value);
      newTotalValues[key].disabled = Number(value) === 0;
    }

    this.totalValues = newTotalValues;
    this.$store.dispatch('performance/SET_OVERVIEW_DATA', newTotalValues);
  }
}
